<template>
  <app-page-section
    class="wrapper"
    :title="{ tag: 'p', text: 'Des outils simples pour chacun' }"
  >
    <template #subtitle>
      <div class="grid place-content-center">
        <app-navigation-tabs
          v-model="activeSection"
          :items="[
            { label: 'Organisateur', value: 'customer' },
            { label: 'Apprenant', value: 'learner' },
          ]"
        />
      </div>
    </template>

    <div class="relative">
      <section
        v-for="section in sections"
        :key="section.id"
        class="place grid aspect-[0.9] w-full cursor-pointer grid-rows-[auto_1fr] gap-12 overflow-hidden rounded-xl pt-6 text-center shadow-lg sm:aspect-[1.4] md:w-[70%] lg:aspect-[1.5] lg:pt-12"
        :class="[section.class, isActive(section.id) ? 'on-front' : 'on-back']"
        @click="activeSection = section.id"
      >
        <div class="px-6">
          <h3
            class="my-4 font-title text-lg/normal md:text-xl/normal xl:text-2xl/normal"
          >
            {{ section.title }}
          </h3>
          <growth-contact-button
            class="!text-inherit"
            origin="enterprise"
            :origin_page_action="`personas_${section.id}_cta`"
            variant="link"
          />
        </div>

        <div class="relative">
          <app-image
            alt=""
            class="absolute left-1/2 h-full w-4/5 -translate-x-1/2 rounded-t-lg object-cover object-left-top"
            :src="section.cover"
          />
        </div>
      </section>
    </div>
  </app-page-section>
</template>

<script lang="ts" setup>
import type { I18nMessage } from "@pollen/core/model/i18n.model";

const sections = computed(() => {
  return [
    {
      id: "customer",
      label: "Pour l'organisateur",
      title:
        "Avec Hub, gérez les inscriptions et mesurez la satisfaction de vos sessions",
      cover:
        "https://res.cloudinary.com/dfdgwwkpd/image/upload/v1729075933/brand/enterprise/79a49231e04c8db69e7a1a7250728be6_i4irf6.png",
      class: "left relative bg-primary-950 text-white origin-left",
    },
    {
      id: "learner",
      label: "Pour l'apprenant",
      title:
        "Dans l’espace Learner, préparez votre session puis retrouvez vos documents et votre certificat",
      cover:
        "https://res.cloudinary.com/dfdgwwkpd/image/upload/v1729075305/brand/enterprise/21159a86ae51b177fee6a9f463831543_d4c2sc.png",
      class:
        "right bg-primary-50 text-primary-950 origin-right absolute right-0 top-0",
    },
  ] as const satisfies {
    id: string;
    label: I18nMessage;
    title: I18nMessage;
    cover: string;
    class: string;
  }[];
});

type PersonaSectionId = (typeof sections.value)[number]["id"];

const activeSection = ref<PersonaSectionId>("customer");

function isActive(id: PersonaSectionId) {
  return activeSection.value === id;
}
</script>

<style scoped>
.wrapper {
  --animationScaleMinimum: 0.8;
  --animationScaleMaximum: 1;
  --animationScaleMedium: calc(
    var(--animationScaleMinimum) +
      (var(--animationScaleMaximum) - var(--animationScaleMinimum)) / 2
  );
}

@keyframes panelAnimation {
  0% {
    transform: scale(var(--scaleStart));
    z-index: var(--zIndexStart);
  }
  50% {
    transform: scale(var(--animationScaleMedium))
      translateX(var(--animationXOffset));
    z-index: var(--zIndexEnd);
  }
  100% {
    transform: scale(var(--scaleEnd)) translateX(0);
    z-index: var(--zIndexEnd);
  }
}

@keyframes panelAnimation2 {
  0% {
    transform: scale(var(--scaleStart));
    z-index: var(--zIndexStart);
  }
  50% {
    transform: scale(var(--animationScaleMedium))
      translateX(var(--animationXOffset));
    z-index: var(--zIndexEnd);
  }
  100% {
    transform: scale(var(--scaleEnd)) translateX(0);
    z-index: var(--zIndexEnd);
  }
}

.left {
  --animationXOffset: -30%;
}

.right {
  --animationXOffset: 30%;
}

.on-front {
  --scaleStart: var(--animationScaleMinimum);
  --scaleEnd: var(--animationScaleMaximum);
  --zIndexStart: 0;
  --zIndexEnd: 1;
  animation-name: panelAnimation;
}

.on-back {
  --scaleStart: var(--animationScaleMaximum);
  --scaleEnd: var(--animationScaleMinimum);
  --zIndexStart: 1;
  --zIndexEnd: 0;
  animation-name: panelAnimation2;
}

.on-front,
.on-back {
  animation-duration: 300ms;
  animation-timing-function: ease-in-out;
  animation-fill-mode: forwards;
}
</style>
